/**
 * What Recents can do to its chats: open, settle, snooze, pin, rename and the
 * rest of T3 Code's thread menu, for one chat or for a multi-selection.
 *
 * @module components/home/useRecentThreadActions
 */
import { useAtomValue } from "@effect/atom-react";
import { scopeThreadRef, scopedThreadKey } from "@amoreg/client-runtime/environment";
import type { EnvironmentThreadShell } from "@amoreg/client-runtime/state/models";
import { isAtomCommandInterrupted, settlePromise, squashAtomCommandFailure } from "@amoreg/client-runtime/state/runtime";
import { canSnooze } from "@amoreg/client-runtime/state/thread-settled";
import type { ContextMenuPosition, ScopedThreadRef } from "@amoreg/contracts";
import { useRouter } from "@tanstack/react-router";
import { type RefObject, useCallback, useMemo, useState } from "react";

import { useClientSettings } from "../../hooks/useSettings";
import { useThreadActions } from "../../hooks/useThreadActions";
import { readLocalApi } from "../../localApi";
import { environmentServerConfigsAtom } from "../../state/server";
import { threadEnvironment } from "../../state/threads";
import { useAtomCommand } from "../../state/use-atom-command";
import { useThreadSelectionStore } from "../../threadSelectionStore";
import { buildThreadRouteParams } from "../../threadRoutes";
import { useUiStateStore } from "../../uiStateStore";
import { requestCustomSnooze } from "../customSnoozeRequest";
import { buildBulkTitleRegenerationContextMenuItem } from "../Sidebar.logic";
import { resolveSnoozePresets, snoozeWakeDescription, type SnoozePreset } from "../Sidebar.snooze";
import { buildThreadActionMenuItems } from "../threadActionMenu.logic";
import { stackedThreadToast, toastManager } from "../ui/toast";
import type { RecentsRow, RecentsSection } from "./HomeSidebarRecents.logic";

export interface RecentThreadHandlers {
  readonly open: (ref: ScopedThreadRef, projectId: string) => void;
  readonly settle: (ref: ScopedThreadRef) => Promise<void>;
  readonly unsettle: (ref: ScopedThreadRef) => Promise<void>;
  readonly pin: (ref: ScopedThreadRef) => Promise<void>;
  readonly unpin: (ref: ScopedThreadRef) => Promise<void>;
  readonly unsnooze: (ref: ScopedThreadRef) => Promise<void>;
  readonly snooze: (ref: ScopedThreadRef, preset: Pick<SnoozePreset, "snoozedUntil">) => Promise<void>;
  readonly startRename: (ref: ScopedThreadRef, title: string) => void;
  readonly commitRename: (ref: ScopedThreadRef, title: string, original: string) => Promise<void>;
  readonly cancelRename: () => void;
  readonly acknowledgeWoke: (ref: ScopedThreadRef, wokeAt: string) => void;
}

const refOf = (thread: EnvironmentThreadShell) => scopeThreadRef(thread.environmentId, thread.id);

export function useRecentThreadActions({
  onNavigate,
  rowByKey,
}: {
  readonly onNavigate: () => void;
  /** The rows on screen, by thread key: a selection can outlive its rows (paging, the filter, deletes elsewhere). */
  readonly rowByKey: RefObject<ReadonlyMap<string, RecentsRow>>;
}) {
  const router = useRouter();
  const serverConfigs = useAtomValue(environmentServerConfigsAtom);
  const timestampFormat = useClientSettings((settings) => settings.timestampFormat);
  const confirmThreadDelete = useClientSettings((settings) => settings.confirmThreadDelete);
  const confirmThreadArchive = useClientSettings((settings) => settings.confirmThreadArchive);
  const markThreadUnread = useUiStateStore((state) => state.markThreadUnread);
  const markThreadVisited = useUiStateStore((state) => state.markThreadVisited);
  const clearSelection = useThreadSelectionStore((state) => state.clearSelection);
  const removeFromSelection = useThreadSelectionStore((state) => state.removeFromSelection);
  const actions = useThreadActions();
  const updateThreadMetadata = useAtomCommand(threadEnvironment.updateMetadata, { reportFailure: false });
  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const [renamingTitle, setRenamingTitle] = useState("");

  const fail = useCallback((title: string, result: unknown) => {
    const typed = result as Parameters<typeof isAtomCommandInterrupted>[0];
    if ((typed as { _tag?: string })._tag !== "Failure" || isAtomCommandInterrupted(typed)) return;
    const error = squashAtomCommandFailure(typed as never);
    toastManager.add(
      stackedThreadToast({
        type: "error",
        title,
        description: error instanceof Error ? error.message : "An error occurred.",
      }),
    );
  }, []);

  const handlers = useMemo(
    (): RecentThreadHandlers => ({
      open: (ref, projectId) => {
        onNavigate();
        void router.navigate({ to: "/$org/$threadId", params: buildThreadRouteParams(ref, projectId) });
      },
      settle: async (ref) => fail("Failed to settle thread", await actions.settleThread(ref)),
      unsettle: async (ref) => fail("Failed to un-settle thread", await actions.unsettleThread(ref)),
      pin: async (ref) => fail("Failed to pin thread", await actions.pinThread(ref)),
      unpin: async (ref) => fail("Failed to unpin thread", await actions.unpinThread(ref)),
      unsnooze: async (ref) => fail("Failed to wake thread", await actions.unsnoozeThread(ref)),
      snooze: async (ref, preset) => {
        const result = await actions.snoozeThread(ref, preset.snoozedUntil);
        if (result._tag === "Failure") return fail("Failed to snooze thread", result);
        toastManager.add(
          stackedThreadToast({
            type: "success",
            title: `Snoozed until ${snoozeWakeDescription(preset.snoozedUntil, new Date(), timestampFormat)}`,
            timeout: 5_000,
            actionProps: { children: "Undo", onClick: () => void actions.unsnoozeThread(ref) },
          }),
        );
      },
      startRename: (ref, title) => {
        setRenamingKey(scopedThreadKey(ref));
        setRenamingTitle(title);
      },
      commitRename: async (ref, title, original) => {
        setRenamingKey(null);
        const trimmedTitle = title.trim();
        if (trimmedTitle.length === 0 || trimmedTitle === original) return;
        fail(
          "Failed to rename thread",
          await updateThreadMetadata({
            environmentId: ref.environmentId,
            input: { threadId: ref.threadId, title: trimmedTitle },
          }),
        );
      },
      cancelRename: () => setRenamingKey(null),
      acknowledgeWoke: (ref, wokeAt) => markThreadVisited(scopedThreadKey(ref), wokeAt),
    }),
    [actions, fail, markThreadVisited, onNavigate, router, timestampFormat, updateThreadMetadata],
  );

  const openThreadMenu = useCallback(
    async (thread: EnvironmentThreadShell, section: RecentsSection, position: ContextMenuPosition) => {
      const api = readLocalApi();
      if (!api) return;
      const ref = refOf(thread);
      const caps = serverConfigs.get(thread.environmentId)?.environment.capabilities;
      const snoozePresets = resolveSnoozePresets(new Date(), timestampFormat);
      const isRegeneratingTitle = thread.titleRegeneration != null;
      const items = buildThreadActionMenuItems({
        branch: null,
        isPinned: thread.pinnedAt != null,
        isSettled: section === "settled",
        isSnoozed: section === "snoozed",
        canSnoozeNow: canSnooze(thread, { now: new Date().toISOString() }),
        isRegeneratingTitle,
        isRunning: thread.session?.status === "running" && thread.session.activeTurnId != null,
        supports: {
          settlement: caps?.threadSettlement === true,
          snooze: caps?.threadSnooze === true,
          pinning: caps?.threadPinning === true,
          titleRegeneration: caps?.threadTitleRegeneration === true,
        },
        snoozePresets,
      }).filter((item) => item.id !== "copy-path");
      const clicked = await api.contextMenu.show(items, position).catch(() => null);
      if (!clicked) return;
      if (clicked.startsWith("snooze:")) {
        const preset =
          clicked === "snooze:custom"
            ? await requestCustomSnooze()
            : snoozePresets.find((candidate) => `snooze:${candidate.id}` === clicked);
        if (preset) void handlers.snooze(ref, preset);
        return;
      }
      switch (clicked) {
        case "pin":
          return void handlers.pin(ref);
        case "unpin":
          return void handlers.unpin(ref);
        case "settle":
          return void handlers.settle(ref);
        case "unsettle":
          return void handlers.unsettle(ref);
        case "unsnooze":
          return void handlers.unsnooze(ref);
        case "rename":
          return handlers.startRename(ref, thread.title);
        case "regenerate-title":
          if (!isRegeneratingTitle)
            fail(
              "Failed to regenerate thread title",
              await updateThreadMetadata({
                environmentId: ref.environmentId,
                input: { threadId: ref.threadId, regenerateTitle: true },
              }),
            );
          return;
        case "mark-unread":
          return markThreadUnread(scopedThreadKey(ref), thread.latestTurn?.completedAt);
        case "copy-thread-id":
          return void navigator.clipboard?.writeText(thread.id);
        case "archive": {
          // The person's own confirmation settings, asked in the app's dialog, as the Chat sidebar did.
          if (confirmThreadArchive) {
            const confirmed = await settlePromise(() => api.dialogs.confirm(`Archive thread "${thread.title}"?`));
            if (confirmed._tag === "Failure" || !confirmed.value) return;
          }
          return fail("Failed to archive thread", await actions.archiveThread(ref));
        }
        case "delete": {
          if (confirmThreadDelete) {
            const confirmed = await settlePromise(() =>
              api.dialogs.confirm(
                [`Delete thread "${thread.title}"?`, "This permanently clears conversation history for this thread."].join("\n"),
              ),
            );
            if (confirmed._tag === "Failure" || !confirmed.value) return;
          }
          return fail("Failed to delete thread", await actions.deleteThread(ref));
        }
        default:
          return;
      }
    },
    [
      actions,
      confirmThreadArchive,
      confirmThreadDelete,
      fail,
      handlers,
      markThreadUnread,
      serverConfigs,
      timestampFormat,
      updateThreadMetadata,
    ],
  );

  /** T3 Code's bulk menu (Sidebar.tsx handleMultiSelectContextMenu) for the selected rows on screen. */
  const openSelectionMenu = useCallback(
    async (position: ContextMenuPosition) => {
      const api = readLocalApi();
      if (!api) return;
      const threadKeys = [...useThreadSelectionStore.getState().selectedThreadKeys].filter((key) =>
        rowByKey.current.has(key),
      );
      if (threadKeys.length === 0) return;
      const count = threadKeys.length;
      const selected = threadKeys.flatMap((key) => {
        const row = rowByKey.current.get(key);
        return row ? [row.thread] : [];
      });
      const selectionNow = new Date().toISOString();
      const canSnoozeSelection = selected.every(
        (thread) =>
          serverConfigs.get(thread.environmentId)?.environment.capabilities.threadSnooze === true &&
          canSnooze(thread, { now: selectionNow }),
      );
      const titleRegenerationThreads = selected.filter(
        (thread) => serverConfigs.get(thread.environmentId)?.environment.capabilities.threadTitleRegeneration === true,
      );
      const regeneratable = titleRegenerationThreads.filter((thread) => thread.titleRegeneration == null);
      const titleItem = buildBulkTitleRegenerationContextMenuItem({
        supportedCount: titleRegenerationThreads.length,
        actionableCount: regeneratable.length,
      });
      const snoozePresets = resolveSnoozePresets(new Date(), timestampFormat);
      const clicked = await settlePromise(() =>
        api.contextMenu.show(
          [
            { id: "settle", label: `Settle (${count})` },
            ...(canSnoozeSelection
              ? [
                  {
                    id: "snooze",
                    label: `Snooze (${count})`,
                    children: [
                      ...snoozePresets.map((preset) => ({
                        id: `snooze:${preset.id}`,
                        label: `${preset.label} (${preset.whenLabel})`,
                      })),
                      { id: "snooze:custom", label: "Custom…" },
                    ],
                  },
                ]
              : []),
            ...(titleItem ? [titleItem] : []),
            { id: "mark-unread", label: `Mark unread (${count})` },
            { id: "delete", label: `Delete (${count})`, destructive: true },
          ],
          position,
        ),
      );
      if (clicked._tag === "Failure" || !clicked.value) return;
      const choice = clicked.value;
      if (choice.startsWith("snooze:")) {
        const preset =
          choice === "snooze:custom"
            ? await requestCustomSnooze()
            : snoozePresets.find((candidate) => `snooze:${candidate.id}` === choice);
        if (!preset) return;
        clearSelection();
        const outcomes = await Promise.all(
          selected.map(async (thread) => ({
            ref: refOf(thread),
            result: await actions.snoozeThread(refOf(thread), preset.snoozedUntil),
          })),
        );
        const snoozedRefs = outcomes.flatMap(({ ref, result }) => (result._tag === "Success" ? [ref] : []));
        const failed = outcomes.length - snoozedRefs.length;
        if (snoozedRefs.length > 0) {
          toastManager.add(
            stackedThreadToast({
              type: failed > 0 ? "warning" : "success",
              title:
                failed > 0
                  ? `Snoozed ${snoozedRefs.length} of ${selected.length} threads`
                  : `Snoozed ${snoozedRefs.length} thread${snoozedRefs.length === 1 ? "" : "s"}`,
              description: failed > 0 ? `${failed} thread${failed === 1 ? "" : "s"} couldn't be snoozed.` : undefined,
              timeout: 5_000,
              actionProps: {
                children: "Undo",
                onClick: () => {
                  for (const ref of snoozedRefs) void actions.unsnoozeThread(ref);
                },
              },
            }),
          );
        } else {
          const firstFailure = outcomes[0]?.result;
          if (firstFailure) fail("Failed to snooze threads", firstFailure);
        }
        return;
      }
      if (choice === "regenerate-title") {
        for (const thread of regeneratable) {
          const result = await updateThreadMetadata({
            environmentId: thread.environmentId,
            input: { threadId: thread.id, regenerateTitle: true },
          });
          if (result._tag !== "Success") return fail("Failed to regenerate thread titles", result);
        }
        clearSelection();
        return;
      }
      if (choice === "settle") {
        for (const thread of selected) {
          if (thread.settledOverride === "settled") continue;
          void handlers.settle(refOf(thread));
        }
        clearSelection();
        return;
      }
      if (choice === "mark-unread") {
        for (const thread of selected) markThreadUnread(scopedThreadKey(refOf(thread)), thread.latestTurn?.completedAt);
        clearSelection();
        return;
      }
      if (choice !== "delete") return;
      if (confirmThreadDelete) {
        const confirmed = await settlePromise(() =>
          api.dialogs.confirm(
            [
              `Delete ${count} thread${count === 1 ? "" : "s"}?`,
              "This permanently clears conversation history for these threads.",
            ].join("\n"),
          ),
        );
        if (confirmed._tag === "Failure" || !confirmed.value) return;
      }
      const deletedThreadKeys = new Set<string>();
      for (const thread of selected) {
        const ref = refOf(thread);
        const result = await actions.deleteThread(ref, { deletedThreadKeys });
        if (result._tag === "Failure") return fail("Failed to delete threads", result);
        deletedThreadKeys.add(scopedThreadKey(ref));
      }
      removeFromSelection(threadKeys);
    },
    [
      actions,
      clearSelection,
      confirmThreadDelete,
      fail,
      handlers,
      markThreadUnread,
      removeFromSelection,
      rowByKey,
      serverConfigs,
      timestampFormat,
      updateThreadMetadata,
    ],
  );

  return {
    handlers,
    renaming: { key: renamingKey, title: renamingTitle, setTitle: setRenamingTitle },
    openThreadMenu,
    openSelectionMenu,
  };
}
